import * as THREE from 'three';
import {clone} from "utils/utils"

const borderSize = 5;

// Opacity applied to actual cargo meshes (boxes, sacks, rolls, pallet decks)
// so the loaded units read as translucent against the container/ground.
export const CARGO_OPACITY = 0.85;

export const degreesToRadians = (degrees) => degrees * Math.PI / 180;

// A "reflection" that ADDS light rather than shading the surface keeps the
// base color exactly as requested — a lit material (Phong/Physical) always
// multiplies the color by scene lighting, which reads as darkening. This
// fresnel rim-light is drawn as a second, additively-blended pass sharing
// the cargo mesh's own geometry, so it only ever brightens edges/highlights
// and never touches the flat base color underneath.
let fresnelOverlayMaterial;
function getFresnelOverlayMaterial() {
  if (fresnelOverlayMaterial) return fresnelOverlayMaterial;

  fresnelOverlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      power: { value: 2.5 },
      intensity: { value: 0.4 },
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      uniform float power;
      uniform float intensity;
      void main() {
        float fresnel = pow(1.0 - max(dot(normalize(vNormal), normalize(vViewDir)), 0.0), power);
        gl_FragColor = vec4(vec3(1.0), fresnel * intensity);
      }
    `,
  });

  return fresnelOverlayMaterial;
}

function addReflectionOverlay(scene, geometry, mesh) {
  const sheen = new THREE.Mesh(geometry, getFresnelOverlayMaterial());
  sheen.position.copy(mesh.position);
  sheen.rotation.copy(mesh.rotation);
  scene.add(sheen);

  // const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  // scene.add(ambientLight);

  // const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
  // directionalLight.position.set(10, 20, 0); // x, y, z
  // scene.add(directionalLight);

}

function getRandomColor() {
  let letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) color += letters[Math.floor(Math.random() * 16)];
  return color;
}
let sizeCoefficient;
/***** MATERIALS *****/

window.materialLib = {};

// Hash function
Object.defineProperty(String.prototype, 'hashCode', {
  value: function () {
    let hash = 0,
      i,
      chr;
    for (i = 0; i < this.length; i++) {
      chr = this.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  },
});

/***** BOX *****/

window.boxMaterial = function (size, color, sizeCoefficient = 1, border = borderSize) {

  let materialHash = JSON.stringify({type: "box", material: "basic", size, color,}).hashCode();

  if (materialLib[materialHash] !== undefined) {
    return materialLib[materialHash];
  }

  const createCanvasBcg = (width, height, color) => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const texture = new THREE.Texture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;

    return texture;
  };
  const xw = size.x * sizeCoefficient;
  const yw = size.y * sizeCoefficient;
  const zw = size.z * sizeCoefficient;

  const textures = [];
  textures.push(
    createCanvasBcg(yw, zw, color, .5),
    createCanvasBcg(xw, yw, color, .5),
    createCanvasBcg(xw, zw, color, .5)
  );

  materialLib[materialHash] = [
    new THREE.MeshBasicMaterial({map: textures[0], toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textures[0], toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textures[1], toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textures[1], toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textures[2], toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textures[2], toneMapped: false}),
  ];

  return materialLib[materialHash];
};

export function createBox(scene, data, sizeCoefficient = 1, index, border) {
  // Cell size: the full slot this box occupies (also the tiling step for
  // repeated data.complex copies). The mesh itself is drawn `gap` smaller
  // and re-centered in that same cell, so neighbors get a visible seam
  // without needing to know about each other.
  const cellX = data.size.x * sizeCoefficient;
  const cellY = data.size.y * sizeCoefficient;
  const cellZ = data.size.z * sizeCoefficient;
  const posX = data.location.x * sizeCoefficient;
  const posY = data.location.y * sizeCoefficient;
  const posZ = data.location.z * sizeCoefficient;

  const gap = data.gap || 0;
  const xw = Math.max(cellX - gap, 0);
  const yw = Math.max(cellY - gap, 0);
  const zw = Math.max(cellZ - gap, 0);

  let cubeGeometry = new THREE.BoxGeometry(xw, zw, yw);
  let cubeMaterials = boxMaterial(data.size, getColor(data), border);

  if (data.opacity) {
    // Unlit on purpose: a lit material (Phong/Physical/Standard) reacts to
    // scene lighting and visibly darkens/shifts the requested color once
    // transparent — MeshBasicMaterial always renders the exact input color.
    // "Reflective" cargo gets its shine from a separate additive overlay
    // (see addReflectionOverlay) instead, so this stays untouched.
    cubeMaterials = new THREE.MeshBasicMaterial({
      color: getColor(data),
      opacity: data.opacity,
      transparent: true,
      toneMapped: false,
    });
  }

  for ( let x = 0; x < data.complex.x; x++ ) {
    for ( let y = 0; y < data.complex.y; y++ ) {
      for ( let z = 0; z< data.complex.z; z++ ) {
        let cube = new THREE.Mesh(cubeGeometry, cubeMaterials);
        cube.position.set(
          posX + x * cellX + cellX / 2,
          posZ + z * cellZ + cellZ / 2,
          posY + y * cellY + cellY / 2,
        );
        if (data.rotation) {
          Object.entries(data.rotation).map(([sign, value]) => {
            if (value) cube.rotation[sign] = degreesToRadians(value);
          })
        }
        if (data.reflective) {
          addReflectionOverlay(scene, cubeGeometry, cube);
        }
        if (data?.name) {
          if (index >= 0) {
            cube.name = data.name + ' ' + index + x + y + z;
          } else {
            cube.name = data.name + ' ' + x + y + z;
          }
        }
        scene.add(cube);
      }
    }
  }
}

function getColor(data) {
  return data.color;
}

/***** CYLINDER  *****/
window.rollMaterial = function (height, radius, color, opacity) {
  let materialHash = JSON.stringify({
    type: "roll",
    height: height,
    radius: radius,
    color: color,
    opacity: opacity,
    material: opacity ? "basic-transparent" : "basic"
  }).hashCode();

  if (materialLib[materialHash] !== undefined) {
    return materialLib[materialHash];
  }

  if (opacity) {
    // See the note in createBox's opacity branch — unlit on purpose.
    const material = new THREE.MeshBasicMaterial({ color, opacity, transparent: true, toneMapped: false });
    materialLib[materialHash] = [material, material, material];
    return materialLib[materialHash];
  }

  const createCanvasBcg = (canvas, ctx, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const canvas = document.createElement('canvas');

  canvas.width = 2 * Math.PI * radius;
  canvas.height =  height;

  const ctx = canvas.getContext('2d');

  const texture = new THREE.Texture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  ctx.imageSmoothingEnabled = false;

  createCanvasBcg(canvas, ctx, color);

  const createCanvasHat = (canvas, ctx, color) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, 2 * Math.PI, false);
    ctx.fill();
  }

  const canvasHat = document.createElement('canvas');

  canvasHat.width = 2 * radius;
  canvasHat.height = 2 * radius;

  const ctxHat = canvasHat.getContext('2d');

  const textureHat = new THREE.Texture(canvasHat);
  textureHat.colorSpace = THREE.SRGBColorSpace;
  textureHat.needsUpdate = true;
  ctxHat.imageSmoothingEnabled = true;

  createCanvasHat(canvasHat, ctxHat, color);

  let cylinderMaterials = [
    new THREE.MeshBasicMaterial({map: texture, toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textureHat, toneMapped: false}),
    new THREE.MeshBasicMaterial({map: textureHat, toneMapped: false}),
  ];

  materialLib[materialHash] = cylinderMaterials;

  return cylinderMaterials;
};
function transformerConfig(xAxis, zAxis) {
  const result = {};
  result[zAxis] = 'z';
  result[xAxis] = 'x'
  if (zAxis === 'z') {
    result[xAxis === 'x' ? 'y' : 'x'] = 'y';
  } else {
    result['z'] = 'y'
  }

  return result;
}

function createCylinder(scene, rollData, sizeCoefficient, index) {
  const {size, complex, location} = rollData;
  const axis = size.axis;
  const convertHeight = size.height * sizeCoefficient;
  const convertRadius = size.radius * sizeCoefficient;
  let cylinderGeometry = new THREE.CylinderGeometry(
    convertRadius,
    convertRadius,
    convertHeight,
    12,
    1
  );
  const xAxis = complex !== null ? complex.xAxis : (axis === 'x' ? 'y': 'x');
  const transformer = transformerConfig(xAxis, axis);
  let cylindersList = [];
  if (null !== rollData.complex) {
    const axisDistance = complex.y > 0
      ? Math.ceil(Math.sqrt(Math.pow(2 * convertRadius, 2) - Math.pow(convertRadius, 2)))
      : 0;
    const sameRow = complex.x % 2 === 1;
    const oddCount = Math.floor(complex.x / 2);
      /*axis for height*/
      for ( let z = 1; z <= complex.z; z++ ) {
        // second axis
        for ( let y = 0; y <= complex.y; y++ ) {
          //base axis
          const oddRow = y % 2 === 0;
          const lineCount = oddCount - (sameRow || oddRow ? 0 : 1)
          for ( let x = 1; x <= lineCount; x++ ) {
            const relPosition = {
                x: convertRadius * (x * 2 - 1 + (oddRow ? 0 : 1)),
                y: convertRadius + axisDistance * y,
                z: convertHeight * (z - 0.5)
              }
            //  трансформировать с учетом заданных осей
            cylindersList.push({
              x: location.x * sizeCoefficient + relPosition[transformer.x],
              y: location.y * sizeCoefficient + relPosition[transformer.y],
              z: location.z * sizeCoefficient + relPosition[transformer.z]
            });
          }
        }
      }
  } else {
    const relPosition = {
      x: convertRadius,
      y: convertRadius,
      z: convertHeight / 2
    }
    cylindersList.push({
      x: location.x * sizeCoefficient + relPosition[transformer.x],
      y: location.y * sizeCoefficient + relPosition[transformer.y],
      z: location.z * sizeCoefficient + relPosition[transformer.z]
    });
  }

  let cylinderMaterials = rollMaterial(convertHeight, convertRadius, getColor(rollData), rollData.opacity);

  cylindersList.forEach( (pos) => {
    let cylinder = new THREE.Mesh(cylinderGeometry, cylinderMaterials);
    cylinder.position.set(pos.x, pos.z, pos.y);
    if (axis === 'x') {
      cylinder.rotation.x += Math.PI / 2;
      cylinder.rotation.z += Math.PI / 2
    } else if (axis === 'y') {
      cylinder.rotation.y += Math.PI / 2;
      cylinder.rotation.z += Math.PI / 2
    }
    if (rollData?.name) {
      if (index >= 0) {
        cylinder.name = rollData.name + ' ' + index + pos.x + pos.y + pos.z;
      } else {
        cylinder.name = rollData.name + ' ' + pos.x + pos.y + pos.z;
      }
    }

    if (rollData.reflective) {
      addReflectionOverlay(scene, cylinderGeometry, cylinder);
    }

    scene.add(cylinder);
  });
}

export function createSceneObjects(
  scene,
  containerObject,
  maxSize,
  coefficient,
  index,
  gap = 0
) {
  let palletSize = containerObject.size;
  let sizeCoefficient = coefficient;
  let pallet = {...containerObject};

  if (!!pallet.children) {
    if (pallet.floorBox) {
      let boxData = {
        size: pallet.floorBox,
        complex: pallet.complex,
        location: pallet.location,
        uid: pallet.uid,
        color: pallet.color || '#a56508',
        opacity: CARGO_OPACITY
      }
      createBox(scene, boxData, sizeCoefficient, index);
    }

    for (let j in pallet.children) {
      let obj = clone(pallet.children[j]);
      obj.location.z += pallet.floorBox.z;
      let objClone = {};
      Object.assign(objClone, obj)
      objClone.opacity = CARGO_OPACITY;
      objClone.gap = gap;
      objClone.reflective = true;

      for (let x = 0; x < pallet.complex.x; ++x) {
        for (let y = 0; y < pallet.complex.y; ++y) {

          objClone.location = {
            x: pallet.location.x + obj.location.x + x * palletSize.x,
            y: pallet.location.y + obj.location.y + y * palletSize.y,
            z: pallet.location.z + obj.location.z
          };

          switch (obj.form) {
            case 'box':
              createBox(scene, objClone, sizeCoefficient, index);
              break;

            case 'roll':
              createCylinder(scene, objClone, sizeCoefficient, index);
              break;
          }
        }
      }
    }

  } else { // no pallet
    // Spread rather than mutate — containerObject is the backend response
    // item, reused across re-renders (steps/storyboard).
    const withOpacity = { ...containerObject, opacity: CARGO_OPACITY, gap, reflective: true };
    switch (containerObject.form) {
      case 'box':
        createBox(scene, withOpacity, sizeCoefficient, index);
        break;
      case 'roll':
        createCylinder(scene, withOpacity, sizeCoefficient, index);
        break;
    }
  }
}
